import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MagnusCrestSvg, MagnusLogoFull } from "./Logo";
import PhotoFrame from "./PhotoFrame";
import { scrollToId, waLink, DEFAULT_WA_MSG } from "../lib/site";
import canecaImg from "../assets/photos/caneca.jpg";
import azulejoImg from "../assets/photos/azulejo.jpg";

const MaskedLine = ({ children, delay, className = "" }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className={`block ${className}`}
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yA = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const yB = useTransform(scrollYProgress, [0, 1], [0, -70]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            id="inicio"
            ref={ref}
            className="relative min-h-screen flex items-center overflow-hidden bg-cream"
        >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(243,232,200,0.55),transparent_55%)]" />
            <MagnusCrestSvg className="absolute -right-24 top-24 w-[420px] opacity-[0.07] pointer-events-none hidden lg:block" />

            <motion.div
                style={{ opacity: fade }}
                className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-24 lg:pt-24 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center"
            >
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.9 }}
                        className="flex items-center gap-4"
                    >
                        <MagnusLogoFull className="h-20 sm:h-24 w-auto" />
                    </motion.div>

                    <h1 className="font-display font-semibold text-ink leading-[1.02] mt-6 text-[clamp(3rem,9vw,6.8rem)]">
                        <MaskedLine delay={0.45}>
                            <span className="text-gold-gradient">Personalize</span>
                        </MaskedLine>
                        <MaskedLine delay={0.6} className="italic font-medium">
                            seu presente!
                        </MaskedLine>
                    </h1>

                    <MaskedLine delay={0.8} className="mt-7">
                        <span className="font-display italic text-2xl sm:text-3xl text-ink/90">
                            Sua ideia, do seu jeito.
                        </span>
                    </MaskedLine>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1, duration: 0.8 }}
                        className="font-body text-base sm:text-lg text-inksoft leading-relaxed mt-6 max-w-xl"
                    >
                        Criamos produtos personalizados para transformar
                        presentes, eventos e momentos especiais em lembranças
                        únicas.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.15, duration: 0.8 }}
                        className="flex flex-wrap items-center gap-4 mt-10"
                    >
                        <button
                            data-testid="hero-cta-primary"
                            onClick={() => scrollToId("orcamento")}
                            className="bg-obsidian text-cream font-body text-xs sm:text-sm font-semibold uppercase tracking-[0.16em] px-8 py-4 rounded-full hover:bg-gold-deep transition-all duration-300 hover:-translate-y-0.5"
                        >
                            Personalize agora
                        </button>
                        <button
                            data-testid="hero-cta-secondary"
                            onClick={() => scrollToId("produtos")}
                            className="border border-ink/25 text-ink font-body text-xs sm:text-sm font-semibold uppercase tracking-[0.16em] px-8 py-4 rounded-full hover:border-gold hover:text-gold-deep transition-all duration-300"
                        >
                            Ver produtos
                        </button>
                        <a
                            data-testid="hero-whatsapp-link"
                            href={waLink(DEFAULT_WA_MSG)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-body text-xs font-semibold uppercase tracking-[0.14em] text-ink/60 hover:text-[#128C7E] transition-colors underline underline-offset-4 decoration-gold/60"
                        >
                            ou fale pelo WhatsApp
                        </a>
                    </motion.div>
                </div>

                <div className="lg:col-span-5 relative hidden sm:block h-[480px] lg:h-[560px]">
                    <motion.div
                        style={{ y: yA }}
                        className="absolute right-0 top-0 w-[68%] rotate-2"
                    >
                        <div className="animate-floaty">
                            <PhotoFrame
                                img={canecaImg}
                                ratio="aspect-[3/4]"
                                testId="hero-frame-1"
                            />
                        </div>
                    </motion.div>
                    <motion.div
                        style={{ y: yB }}
                        className="absolute left-0 bottom-4 w-[52%] -rotate-3"
                    >
                        <PhotoFrame
                            img={azulejoImg}
                            ratio="aspect-square"
                            testId="hero-frame-2"
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.4, duration: 1 }}
                        className="absolute -bottom-2 right-6 font-display italic text-lg text-gold-deep/80"
                    >
                        feitos especialmente para você
                    </motion.div>
                </div>
            </motion.div>

            <motion.button
                data-testid="hero-scroll-hint"
                onClick={() => scrollToId("o-que-fazemos")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gold-deep hidden md:block"
                aria-label="Rolar para baixo"
            >
                <ArrowDown size={20} className="animate-bounce" />
            </motion.button>
        </section>
    );
}
